/**
 * Statistical primitives for session analytics.
 *
 * Design constraints:
 * - LLM-derived scores are ordinal and non-normal → rank-based methods
 *   (Spearman, Theil-Sen, Mann-Kendall) instead of Pearson/OLS.
 * - Series are short (often n < 30) → permutation tests instead of
 *   asymptotic p-values where feasible.
 * - Many simultaneous tests (co-occurrence matrices) → Benjamini-Hochberg
 *   false-discovery-rate control.
 * - Results must be reproducible run-to-run → seeded PRNG for permutations.
 */

/** Deterministic PRNG (mulberry32) so permutation p-values are reproducible. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Ranks with average ties (1-based). */
export function rank(values: number[]): number[] {
  const idx = values.map((v, i) => [v, i] as const).sort((a, b) => a[0] - b[0]);
  const ranks = new Array<number>(values.length);
  let i = 0;
  while (i < idx.length) {
    let j = i;
    while (j + 1 < idx.length && idx[j + 1][0] === idx[i][0]) j++;
    const avg = (i + j + 2) / 2; // ranks are 1-based
    for (let k = i; k <= j; k++) ranks[idx[k][1]] = avg;
    i = j + 1;
  }
  return ranks;
}

function pearson(xs: number[], ys: number[]): number {
  const n = xs.length;
  if (n < 2) return NaN;
  let sx = 0, sy = 0, sxy = 0, sx2 = 0, sy2 = 0;
  for (let i = 0; i < n; i++) {
    sx += xs[i]; sy += ys[i];
    sxy += xs[i] * ys[i];
    sx2 += xs[i] * xs[i]; sy2 += ys[i] * ys[i];
  }
  const denom = Math.sqrt((n * sx2 - sx * sx) * (n * sy2 - sy * sy));
  return denom === 0 ? 0 : (n * sxy - sx * sy) / denom;
}

/** Spearman rank correlation (rho). Handles ties via average ranks. */
export function spearman(xs: number[], ys: number[]): number {
  if (xs.length !== ys.length || xs.length < 2) return NaN;
  return pearson(rank(xs), rank(ys));
}

/**
 * Two-sided permutation p-value for Spearman rho.
 * Shuffles ys against xs `iterations` times with a seeded PRNG and counts
 * permutations with |rho_perm| >= |rho_observed|. Add-one smoothing keeps
 * p > 0.
 */
export function spearmanPermutationP(
  xs: number[],
  ys: number[],
  iterations = 1000,
  seed = 42
): { rho: number; p: number } {
  const rho = spearman(xs, ys);
  if (!Number.isFinite(rho)) return { rho: NaN, p: NaN };
  const rng = mulberry32(seed);
  const ysCopy = ys.slice();
  let extreme = 0;
  for (let it = 0; it < iterations; it++) {
    // Fisher-Yates shuffle
    for (let i = ysCopy.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [ysCopy[i], ysCopy[j]] = [ysCopy[j], ysCopy[i]];
    }
    if (Math.abs(spearman(xs, ysCopy)) >= Math.abs(rho) - 1e-12) extreme++;
  }
  return { rho, p: (extreme + 1) / (iterations + 1) };
}

/**
 * Benjamini-Hochberg adjusted p-values (q-values).
 * Returns q in the original order of `pvals`.
 */
export function benjaminiHochberg(pvals: number[]): number[] {
  const m = pvals.length;
  if (m === 0) return [];
  const order = pvals
    .map((p, i) => [p, i] as const)
    .sort((a, b) => a[0] - b[0]);
  const q = new Array<number>(m);
  let prev = 1;
  for (let k = m - 1; k >= 0; k--) {
    const [p, origIdx] = order[k];
    const val = Math.min(prev, (p * m) / (k + 1));
    q[origIdx] = val;
    prev = val;
  }
  return q;
}

/**
 * Theil-Sen estimator: median of pairwise slopes. Robust to outliers.
 * xs are typically session indices or day offsets.
 */
export function theilSen(xs: number[], ys: number[]): { slope: number; intercept: number } {
  const n = xs.length;
  if (n < 2 || ys.length !== n) return { slope: NaN, intercept: NaN };
  const slopes: number[] = [];
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const dx = xs[j] - xs[i];
      if (dx !== 0) slopes.push((ys[j] - ys[i]) / dx);
    }
  }
  if (slopes.length === 0) return { slope: NaN, intercept: NaN };
  const slope = median(slopes);
  const intercept = median(ys.map((y, i) => y - slope * xs[i]));
  return { slope, intercept };
}

/**
 * Mann-Kendall trend test (two-sided, normal approximation with tie
 * correction and continuity correction). Standard nonparametric test for
 * monotonic trend in short time series.
 */
export function mannKendall(values: number[]): { s: number; z: number; p: number } {
  const n = values.length;
  if (n < 4) return { s: NaN, z: NaN, p: NaN };

  let s = 0;
  for (let i = 0; i < n - 1; i++) {
    for (let j = i + 1; j < n; j++) {
      s += Math.sign(values[j] - values[i]);
    }
  }

  // Tie correction for variance
  const counts = new Map<number, number>();
  for (const v of values) counts.set(v, (counts.get(v) || 0) + 1);
  let tieTerm = 0;
  counts.forEach((t) => {
    if (t > 1) tieTerm += t * (t - 1) * (2 * t + 5);
  });
  const variance = (n * (n - 1) * (2 * n + 5) - tieTerm) / 18;
  if (variance <= 0) return { s, z: 0, p: 1 };

  const z =
    s > 0 ? (s - 1) / Math.sqrt(variance) : s < 0 ? (s + 1) / Math.sqrt(variance) : 0;
  const p = 2 * (1 - normalCdf(Math.abs(z)));
  return { s, z, p: Math.min(1, Math.max(0, p)) };
}

/** Standard normal CDF via Abramowitz-Stegun erf approximation. */
export function normalCdf(z: number): number {
  const t = 1 / (1 + 0.2316419 * Math.abs(z));
  const d = 0.3989422804014327 * Math.exp((-z * z) / 2);
  let prob =
    d * t * (0.31938153 + t * (-0.356563782 + t * (1.781477937 + t * (-1.821255978 + t * 1.330274429))));
  if (z > 0) prob = 1 - prob;
  return prob;
}

/**
 * RMSSD — root mean square of successive differences. The standard
 * instability measure in affect-dynamics research (and the same construct
 * clinicians know from HRV). Unlike SD it does not count a steady trend
 * as "volatility".
 */
export function rmssd(values: number[]): number {
  if (values.length < 2) return NaN;
  let sum = 0;
  for (let i = 1; i < values.length; i++) {
    const d = values[i] - values[i - 1];
    sum += d * d;
  }
  return Math.sqrt(sum / (values.length - 1));
}

/** First differences: v[i] - v[i-1]. Detrends before correlating. */
export function firstDifferences(values: number[]): number[] {
  const out: number[] = [];
  for (let i = 1; i < values.length; i++) out.push(values[i] - values[i - 1]);
  return out;
}

export function median(values: number[]): number {
  if (values.length === 0) return NaN;
  const s = values.slice().sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 === 1 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

export function mean(values: number[]): number {
  if (values.length === 0) return NaN;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

/** Shannon entropy (bits) of a discrete distribution given raw counts. */
export function shannonEntropy(counts: number[]): number {
  const total = counts.reduce((a, b) => a + b, 0);
  if (total <= 0) return 0;
  let h = 0;
  for (const c of counts) {
    if (c <= 0) continue;
    const p = c / total;
    h -= p * Math.log2(p);
  }
  return h;
}

/** Sample lag-1 autocorrelation (AR1). Rising AR1 in a moving window is the
 * classic "critical slowing down" early-warning signal for state transitions. */
export function lag1Autocorrelation(values: number[]): number {
  const n = values.length;
  if (n < 3) return NaN;
  const m = mean(values);
  let num = 0;
  let den = 0;
  for (let i = 0; i < n; i++) {
    const d = values[i] - m;
    den += d * d;
    if (i < n - 1) num += d * (values[i + 1] - m);
  }
  return den === 0 ? 0 : num / den;
}

/** Sample variance (n-1 denominator). */
export function variance(values: number[]): number {
  const n = values.length;
  if (n < 2) return NaN;
  const m = mean(values);
  return values.reduce((s, v) => s + (v - m) * (v - m), 0) / (n - 1);
}

/** Two-sided critical t at alpha=.05 (small lookup, conservative tail). */
export function tCritical95(df: number): number {
  const table: Record<number, number> = {
    1: 12.71, 2: 4.30, 3: 3.18, 4: 2.78, 5: 2.57,
    6: 2.45, 7: 2.36, 8: 2.31, 9: 2.26, 10: 2.23,
  };
  if (df <= 0) return Infinity;
  if (df <= 10) return table[df];
  if (df <= 20) return 2.09;
  if (df <= 30) return 2.04;
  return 1.96;
}

export interface SuddenShift {
  /** index of the post-shift observation */
  index: number;
  magnitude: number;
  type: "gain" | "loss";
}

/**
 * Sudden-gains detection (Tang & DeRubeis 1999 criteria, generalized):
 *  1. absolute between-session change >= minAbs
 *  2. relative change >= 25% of the pre-shift level
 *  3. the mean of up to 3 pre-shift sessions differs significantly from the
 *     mean of up to 3 post-shift sessions (two-sample t vs critical t at .05)
 *
 * `lowerIsBetter` controls which direction counts as a gain (symptom scales)
 * — the reverse direction is reported as a "loss" (sudden deterioration).
 */
export function suddenShifts(
  values: number[],
  minAbs: number,
  lowerIsBetter: boolean
): SuddenShift[] {
  const n = values.length;
  const out: SuddenShift[] = [];
  for (let i = 1; i < n; i++) {
    const pre = values[i - 1];
    const change = values[i] - pre;
    if (Math.abs(change) < minAbs) continue;
    if (pre !== 0 && Math.abs(change) < 0.25 * Math.abs(pre)) continue;

    const preWin = values.slice(Math.max(0, i - 3), i);
    const postWin = values.slice(i, Math.min(n, i + 3));
    if (preWin.length < 2 || postWin.length < 2) continue;
    const m1 = mean(preWin);
    const m2 = mean(postWin);
    const v1 = variance(preWin);
    const v2 = variance(postWin);
    const df = preWin.length + postWin.length - 2;
    const pooled = Math.sqrt(
      ((preWin.length - 1) * v1 + (postWin.length - 1) * v2) / df
    );
    const se = pooled * Math.sqrt(1 / preWin.length + 1 / postWin.length);
    if (!(se > 0)) continue;
    const tStat = Math.abs(m1 - m2) / se;
    if (tStat < tCritical95(df)) continue;

    const improved = lowerIsBetter ? change < 0 : change > 0;
    out.push({
      index: i,
      magnitude: Math.round(Math.abs(change) * 10) / 10,
      type: improved ? "gain" : "loss",
    });
  }
  return out;
}
